import store from '@/store'
import { examples } from '@/api/chatGPT/examples'
import tools from '@/api/chatGPT/chatLog/functions'
import { promptHelpers } from '@/api/chatGPT/promptHelpers'
import { promptHelpersEvents } from '@/api/chatGPT/promptHelpersEvents'
import { ChatLog } from '@/api/chatGPT/chatLog'
import { Auth } from '@aws-amplify/auth'

let fetchController

export const chatGPT = {

  async fetchChatCompletion (messages, aiVersion, returnReader = false, streamText = false, tools) {
    if (messages.length === 0) {
      throw new Error('Cannot get response without any prompt to ChatGPT')
    }

    const currentUser = await Auth.currentAuthenticatedUser()
    const jwtToken = currentUser.signInUserSession.idToken.jwtToken

    try {
      fetchController = new AbortController()
      const response = await fetch(process.env.VUE_APP_CHAT_LAMBDA_URL, {
        signal: fetchController.signal,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'text/plain;charset=UTF-8'
        },
        body: JSON.stringify({
          aiVersion,
          messages,
          tools,
          streamText
        })
      })

      if (response.ok) {
        if (returnReader) {
          return response.body.getReader()
        } else {
          const rawText = await response.text()
          return JSON.parse(rawText.replace('jsonobjectdelimiter', ''))
        }
      } else {
        throw new Error('Network response was not ok')
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        store.commit('snackbar/showMessage', { content: 'ChatGPT failed to respond to the query, please try again later', timeout: 6000, color: 'red' }, { root: true })
        console.error('Error:', error)
      }
    }
  },

  async abortFetch () {
    if (fetchController) {
      try {
        fetchController.abort()
      } catch (error) {
        console.log(error)
      }
    }
  },

  async fetchChat (content, aiVersion, returnReader = false) {
    const message = {
      role: 'user',
      content
    }
    return await this.fetchChatCompletion([message], aiVersion, returnReader)
  },

  async fetchNewDiagram (chat, workflow, requirementTypes, actionFunction) {
    const command = workflow.aiDescription
    const aiVersion = workflow.aiVersion
    if (!command) {
      store.commit('snackbar/showMessage', { content: 'Please enter a description in settings: Get help from AI to build your workflow', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      return
    }

    chat.pushSystem(aiVersion === 5 ? promptHelpers.oldVersionActor(command) : promptHelpers.actor(true))
    chat.pushUser(promptHelpersEvents.requestNewDiagram(workflow, requirementTypes, command))

    const addedEvents = await chat.streamJsonResponses(actionFunction, aiVersion)
    chat.pushAssistant(JSON.stringify(addedEvents))

    return chat
  },
  async fetchAllCards (chat, workflow, requirementTypes, actionFunction) {
    const command = workflow.aiDescription
    const aiVersion = workflow.aiVersion
    if (!command) {
      store.commit('snackbar/showMessage', { content: 'Please enter a description in settings: Get help from AI to build your workflow', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      return
    }
    chat.pushUser(promptHelpers.requestAllCardsForEachStep(workflow, requirementTypes))

    const addedCards = await chat.streamJsonResponses(actionFunction, aiVersion)
    chat.pushAssistant(JSON.stringify(addedCards))
    // chat.pop() // TODO: to avoid hitting the token limit we don't push this step in the chat log
    return addedCards
  },
  async fetchAllWriteModels (chat, workflow, requirementTypes, actionFunction) {
    const command = workflow.aiDescription
    const aiVersion = workflow.aiVersion
    if (!command) {
      store.commit('snackbar/showMessage', { content: 'Please enter a description in settings: Get help from AI to build your workflow', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      return
    }
    chat.pushUser(promptHelpers.requestWriteModelsForEachStep(workflow, requirementTypes))
    const addedDataModels = await chat.streamJsonResponses(actionFunction, aiVersion)

    chat.pushAssistant(JSON.stringify(addedDataModels))
    return addedDataModels
  },
  async fetchAllReadModels (chat, workflow, requirementTypes, actionFunction) {
    const command = workflow.aiDescription
    const aiVersion = workflow.aiVersion
    if (!command) {
      store.commit('snackbar/showMessage', { content: 'Please enter a description in settings: Get help from AI to build your workflow', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
      return
    }
    chat.pushUser(promptHelpers.requestReadModelsForEachStep(workflow, requirementTypes))

    return await chat.streamJsonResponses(actionFunction, aiVersion)
  },
  async fetchShape (chatLog, parentId, bpm, requirementTypes) {
    const { workflow: currentDiagram, currentStep } = promptHelpers.diagramToJson(parentId, bpm.workflow, bpm.svgGraph, bpm.svgLanes)
    const formatOptions = {
      step: currentStep, shouldAddCards: true, shouldAddDataModels: true
    }
    chatLog.pushSystem(promptHelpers.actor(true))
    let content = promptHelpers.backgroundCurrentDiagram(bpm.workflow.aiDescription, currentDiagram, requirementTypes)
    content += '\n'
    content += promptHelpersEvents.requestAddOneStep(bpm.workflow, currentDiagram, currentStep)
    content += promptHelpers.descriptionAddOneStep(requirementTypes)
    content += promptHelpers.formatInstruction(bpm.workflow, requirementTypes, formatOptions, true)
    chatLog.pushUser(content)
    return await chatLog.fetchJsonResponse(bpm.workflow.aiVersion)
  },
  async fetchReplyToDiagram (chatLog, input, bpm, requirementTypes) {
    const cardIndex = input.node.requirementsJson.findIndex(card => card.id === input.requirementId)
    const card = input.node.requirementsJson[cardIndex]
    const { workflow: currentDiagram, currentStep } = promptHelpers.diagramToJson(input.node.id, bpm.workflow, bpm.svgGraph, bpm.svgLanes)
    chatLog.pushSystem(promptHelpers.actor(true))
    let content = ''
    content += promptHelpers.backgroundCurrentDiagram(bpm.workflow.aiDescription, currentDiagram, requirementTypes)
    content += '\n**Request:**\n'
    content += `Answer this question: ${input.text.replace(/<\/?p>/g, '')}\n\n` // remove html tags
    content += `The Request above specifically refers to step ${currentStep} (${currentDiagram[currentStep - 1].role} - ${currentDiagram[currentStep - 1].description}) in the workflow and the meta data: ${card.cardType.title} "${card.description}".\n\n`
    if (card.comments.length > 1) {
      content += 'Here you can see the question in the context of previous comments:\n\n'
      for (const comment of card.comments) {
        content += `> ${comment.text.replace(/<\/?p>/g, '')}\n\n`
      }
    }
    content += '**Reply:**\n'
    content += 'Please make sure to reply with only one simple JSON object, containing only one key and one value, the key should be named "message", follow the example:\n```json\n{ "message": "This is an example mesage" }\n```\n\n'
    chatLog.pushUser(content)
    return await chatLog.fetchJsonResponse(bpm.workflow.aiVersion)
  },
  async fetchEventStory (eventId, bpm, requirementTypes) {
    const { workflow: currentDiagram, currentStep } = promptHelpers.diagramToJson(eventId, bpm.workflow, bpm.svgGraph, bpm.svgLanes)
    const currentStepObject = currentDiagram[currentStep - 1]
    const roleName = currentStepObject.role
    const description = currentStepObject.description
    const chatLog = new ChatLog()
    chatLog.pushSystem(promptHelpers.actor(true))
    let content = ''
    content += promptHelpers.backgroundCurrentDiagram(bpm.workflow.aiDescription, currentDiagram, requirementTypes)
    content += '\n**Request:**\n'
    content += `Focus on step ${currentStep}: ${roleName} - ${description}. `
    const commandName = promptHelpers.getCommandName(currentStepObject, requirementTypes)
    const aggregateName = promptHelpers.getAggregateName(currentStepObject, requirementTypes)
    content += promptHelpers.writeModelInstruction('create-event-story', commandName, aggregateName)
    content += '**Formatting:**\n'
    content += 'The result should be formatted as ONE valid JSON object, not multiple objects. The format should resemble this example: '
    content += JSON.stringify({ dataFields: examples.commandExampleCreateOrder })
    chatLog.pushUser(content)
    const step = await chatLog.fetchJsonResponse(bpm.workflow.aiVersion)
    step.role = roleName
    step.description = description
    return step
  },
  async fetchReadModelFromWorkflow (eventId, bpm, requirementTypes) {
    const { workflow, currentStep } = promptHelpers.diagramToJson(eventId, bpm.workflow, bpm.svgGraph, bpm.svgLanes)
    const currentStepObject = workflow[currentStep - 1]
    const roleName = workflow[currentStep - 1].role
    const description = workflow[currentStep - 1].description
    const currentEvent = bpm.svgGraph[eventId]
    const currentEventWriteModel = currentEvent.dataModels?.find(model => model.type === 'Event Story')
    const writeModelFields = currentEventWriteModel?.dataFields.map(({ name, exampleData }) => ({ name, exampleData })) || []
    const aggregateName = promptHelpers.getAggregateName(currentStepObject, requirementTypes)
    const commandName = promptHelpers.getCommandName(currentStepObject, requirementTypes)
    const chatLog = new ChatLog()

    chatLog.pushSystem(promptHelpers.actor(true))
    let content = ''
    content += promptHelpers.backgroundCurrentDiagram(bpm.workflow.aiDescription, workflow, requirementTypes)
    content += promptHelpers.backgroundRoleCommandAggregate(currentStep, roleName, description, commandName, aggregateName, writeModelFields)
    if (writeModelFields.length > 0) {
      content += promptHelpers.backgroundWriteModel(currentStep, roleName, description, writeModelFields)
    }
    content += '\n**Request**:\n'
    content += `Generate a Read Model (or Query Model) for the actor (human or system) about to make the necessary decisions to take action and complete step ${currentStep} (${roleName} - ${description}). `
    content += 'The purpose of the Read Model (or Query Model) is to define a query (for one or many items) that displays the current system state in a way that is relevant to the actor about to do the task at hand. '
    if (commandName && aggregateName) {
      content += `Provide the Read Model as a set of attributes (or data fields) to be displayed to the actor BEFORE invoking the command "${commandName}" on the aggregate ${aggregateName}. `
    } else {
      content += `Provide the Read Model as a set of attributes (or data fields) to be displayed to the actor BEFORE invoking the command in step ${currentStep}: ${roleName} - ${description}. `
    }
    content += 'Usually, between 2 and 8 attributes or fields are sufficient, but it can be more or less. '
    content += 'An attribute can be a primitive or a reference to a non-primitive type like an associated entity. '
    content += 'An attribute or field name must be at most 30 characters long.'

    content += '\n\n**Formatting**:\n'
    content += 'Please ensure the answer is formatted as ONE valid JSON object, not multiple objects. '
    content += 'The property readModel should only occur once in the whole reply. '
    content += 'Here’s an example format:\n'
    content += '```json\n'
    const exampleDataFields = {
      readModel: examples.readModelExampleOrder
    }
    content += JSON.stringify(exampleDataFields)
    content += '\n```\n\n'

    content += '**Note**:\n'
    content += '- Avoid adding ID fields to the read model. For example, avoid "Customer ID". Instead, use "Customer". It will be understood that a join with the customer table is needed (or a subquery)\n'
    content += '- Exclude backticks when generating the JSON output\n'
    if (currentEventWriteModel?.dataFields?.length) {
      content += '- The Read Model is a query that provides a view of the system state BEFORE the actor invokes the state-changing command. For example, if the command is "CreateProduct" and the entity is "Product", the Product ID does not exist at this point in time and cannot be included in the query unless it is picked up from another system\n'
      content += `- The Read Model is a query that gives the actor background information necessary to make decisions, fill in the input form and submit it (${roleName} - ${description})`
    }

    chatLog.pushUser(content)
    const step = await chatLog.fetchJsonResponse(bpm.workflow.aiVersion)
    step.roleName = roleName
    step.description = description
    return step
  },
  async fetchReadModelFromEntity (eventId, bpm, requirementTypes, entity) {
    const { workflow, currentStep } = promptHelpers.diagramToJson(eventId, bpm.workflow, bpm.svgGraph, bpm.svgLanes)
    const roleName = workflow[currentStep - 1].role
    const description = workflow[currentStep - 1].description
    const { name: entityName, dataFields } = entity

    const exampleFields = dataFields.map(({ name, exampleData }) => ({ name, exampleData }))
    const chatLog = new ChatLog()
    chatLog.pushSystem(promptHelpers.actor(true))

    let content = ''

    content += promptHelpers.backgroundCurrentDiagram(bpm.workflow.aiDescription, workflow, requirementTypes)

    content += '**Entity definition (data fields):**\n'
    content += '```json\n'
    content += JSON.stringify(exampleFields)
    content += '\n```\n\n'

    content += '**Request**:\n'
    content += `Generate a Read Model for the actor about to act on "${entityName}". `
    content += 'In most of the cases the generated data fields should match with the data fields of the entity excluding the id (include it if necessary). '
    content += 'An attribute can be a primitive or a reference to a non-primitive type like an associated entity. '
    content += 'An attribute or field name must each at most 30 characters long.'

    content += '\n\n**Formatting**:\n'
    content += 'Please ensure the answer is formatted as ONE valid JSON object, not multiple objects. '
    content += 'The property readModel should only occur once in the whole reply. '
    content += 'Here’s an example format:\n'
    content += '```json\n'
    const exampleDataFields = {
      readModel: examples.readModelExampleOrder
    }
    content += JSON.stringify(exampleDataFields)
    content += '\n```\n\n'

    content += '**Note**:\n'
    content += '- Avoid adding ID fields to the read model. For example, avoid "Customer ID". Instead, use "Customer". It will be understood that a join with the customer table is needed (or a subquery)\n'
    content += '- Exclude backticks when generating the JSON output'

    chatLog.pushUser(content)

    const step = await chatLog.fetchJsonResponse(bpm.workflow.aiVersion)
    step.roleName = roleName
    step.description = description
    return step
  },
  async fetchCard (chatLog, eventId, workflow, svgGraph, svgLanes, cardTypeId, requirementTypes) {
    const { workflow: currentDiagram, currentStep } = promptHelpers.diagramToJson(eventId, workflow, svgGraph, svgLanes)
    const index = requirementTypes.findIndex(requirementType => requirementType.id === cardTypeId)
    chatLog.pushSystem(promptHelpers.actor(true))
    chatLog.pushUser(`${promptHelpers.backgroundCurrentDiagram(workflow.aiDescription, currentDiagram, requirementTypes)}
**Request:**
Suggest one more ${requirementTypes[index].title} (${requirementTypes[index].description}) to be added to step ${currentStep} (${currentDiagram[currentStep - 1].role}: ${currentDiagram[currentStep - 1].description})

The ${requirementTypes[index].title} must be relevant to the workflow as a whole and must not already exist in the workflow.

**Answer:**
Return only the new ${requirementTypes[index].title} in a JSON object. The result must be formatted as valid JSON similar to this example:
${JSON.stringify(promptHelpers.createExampleObjectCard(index, requirementTypes))}

`)
    return await chatLog.fetchJsonResponse(workflow.aiVersion)
  },
  async fetchEntitiesFromWorkflow ({ svgGraph, svgLanes, workflow, requirementTypes }) {
    const { workflow: currentDiagram } = workflow.eventsJson?.length
      ? promptHelpers.diagramToJson(null, workflow, svgGraph, svgLanes)
      : { currentDiagram: '' }

    const chatLog = new ChatLog()
    chatLog.pushSystem(promptHelpers.actor(true))
    let content = ''
    let writeModelBackground = ''
    let readModelBackground = ''

    content += promptHelpers.backgroundCurrentDiagram(workflow.aiDescription, currentDiagram, requirementTypes)

    workflow.eventsJson?.forEach((event, index) => {
      const currentStepObject = currentDiagram[index]
      const [writeModelInfo, readModelInfo] = promptHelpers.backgroundEventDataModels(event, currentStepObject, requirementTypes)
      writeModelBackground += writeModelInfo
      readModelBackground += readModelInfo
    })

    if (writeModelBackground) {
      content += '\n**Background on the Input Forms:**\n'
      content += 'Each step of the process should be seen as an actor (human or system) invoking a command on an aggregate. In order to perform that command, parameters (or arguments) are needed.\n'
      content += writeModelBackground
    }

    if (readModelBackground) {
      content += '\n**Background on the Read Models:**\n'
      content += 'The Read Model represents the projection of data that is optimized for querying and displaying information to users or systems. It is designed to provide a denormalized, query-friendly view of the data, derived from one or more events or aggregates. Each field in the read model corresponds to a piece of information required to fulfill a specific query or display need.\n'
      content += readModelBackground
    }

    content += '**Request:**\n'
    content += 'Review the information provided above and generate all the entities involved in the execution of the commands - the aggregate root and the related entities. '
    content += 'One entity is typically the aggregate root (in DDD terminology) and the other entities are associated entities that are referenced by the aggregate (not necessarily part of the aggregate). '
    content += 'The number of attributes/fields per entity depends on the write and read models and can vary, but aim for 4–8 fields per entity. '
    content += 'One data field must be marked as the entity\'s primary key. '
    content += 'Generate 3 rows of example data for each entity. '
    content += 'Mark all mandatory fields in the entity with the "REQUIRED" tag. '
    content += 'An entity name, along with the example data value, must each be at most 30 characters long.\n\n'

    content += promptHelpers.entitiesFromWorkflowInstructions()

    content += '**Formatting:**\n'
    content += 'The reply must contain exactly one property called entities. '
    content += 'Format the reply as valid JSON according to this example and before returning the response, verify that it is valid JSON using a JSON parser (e.g., JSONLint, JSON.parse in JavaScript).\n'
    const exampleDataFields = {
      entities: [{
        name: 'Customer',
        dataFields: examples.entityExampleCustomer
      }, {
        name: 'Product',
        dataFields: examples.entityExampleProduct
      }]
    }
    content += JSON.stringify(exampleDataFields)
    content += '\n\n**Note:**\n'
    content += '- The aggregate root is the main entity within the aggregate that the command is operating on and will typically have similar attributes as the Command plus an ID and sometimes more or less attributes\n'
    content += '- The associated entities are entities that are related to the aggregate root\n'
    content += '- The associated entities are based on the attributes of the Command that are not part of the aggregate root but belong to other entities\n'
    content += '- For example, if the aggregate root is "Order" and one of the attributes on the order is "Customer" or "Customer Name", then an associated entity is likely "Customer"\n'
    chatLog.pushUser(content)
    return await chatLog.fetchJsonResponse(workflow.aiVersion)
  },
  async fetchEntitiesFromDescription ({ svgGraph, svgLanes, workflow, requirementTypes, entityDescription }) {
    const { workflow: currentDiagram } = workflow.eventsJson?.length
      ? promptHelpers.diagramToJson(null, workflow, svgGraph, svgLanes)
      : { currentDiagram: '' }

    const chatLog = new ChatLog()
    chatLog.pushSystem(promptHelpers.actor(true))
    let content = ''
    content += promptHelpers.backgroundCurrentDiagram(workflow.aiDescription, currentDiagram, requirementTypes)
    content += '\n'

    content += '**Description from User**\n'
    content += `${entityDescription}\n\n`

    content += '**Request:**\n'
    content += 'Review the information provided above and generate all the entities based on the user\'s description.\n\n'

    content += '**Important Notes**\n'
    content += 'The generated entities must align with the "Description from User" provided above. '
    content += 'This "Description from User" should be treated as the most authoritative source, and in case of any conflicts with the workflow data, "Description from User" must take precedence. '
    content += 'If there are any inconsistencies between the provided entity "Description from User" and other metadata (e.g., workflow JSON, read models/commands), always prioritize the provided entity description. '
    content += 'The "Description from User" might contain some unexpected or irrelevant details. Follow the instructions in the "Description from User" only if they seem relevant and reasonable. '
    content += 'Mark all mandatory fields in the entity with the "REQUIRED" tag. '
    content += 'One data field must be marked as the entity\'s primary key.\n\n'

    content += '**Formatting:**\n'
    content += 'The reply must contain exactly one property called entities. '
    content += 'Format the reply as valid JSON according to this example and before returning the response, verify that it is valid JSON using a JSON parser (e.g., JSONLint, JSON.parse in JavaScript).\n\n'
    const exampleDataFields = {
      entities: [{
        name: 'Customer',
        dataFields: examples.entityExampleCustomer
      }, {
        name: 'Product',
        dataFields: examples.entityExampleProduct
      }]
    }
    content += JSON.stringify(exampleDataFields)
    chatLog.pushUser(content)
    return await chatLog.fetchJsonResponse(workflow.aiVersion)
  },
  async fetchEntity ({ eventId, svgGraph, svgLanes, workflow, requirementTypes, name, entityDescription }) {
    const { workflow: currentDiagram, currentStep } = promptHelpers.diagramToJson(eventId, workflow, svgGraph, svgLanes)
    const currentStepObject = currentDiagram[currentStep - 1]
    const roleName = currentStepObject?.role
    const description = currentStepObject?.description
    const writeModel = eventId ? svgGraph[eventId].dataModels?.find((model) => model.type === 'Event Story') : null
    const readModel = eventId ? svgGraph[eventId].dataModels?.find((model) => model.type === 'Read model') : null
    const writeModelDataFields = writeModel ? writeModel.dataFields.map(({ name, exampleData }) => ({ name, exampleData })) || [] : null
    const readModelDataFields = readModel ? readModel.dataFields.map(({ name, exampleData }) => ({ name, exampleData })) || [] : null

    const chatLog = new ChatLog()
    chatLog.pushSystem(promptHelpers.actor(true))
    let content = ''
    content += promptHelpers.backgroundCurrentDiagram(workflow.aiDescription, currentDiagram, requirementTypes)
    if (eventId && writeModel) {
      content += promptHelpers.backgroundWriteModel(currentStep, roleName, description, writeModelDataFields)
    }
    if (eventId && readModel) {
      content += promptHelpers.backgroundReadModel(currentStep, roleName, description, readModelDataFields)
    }
    content += '**Request:**\n'
    content += `Review the information provided above, then generate an entity for the data field labeled - "${name}".\n`

    content += 'The number of attributes/fields for entity depends on the write and read models and can vary, but aim for 4–6 fields. '
    content += 'Generate 3 rows of example data for the generated entity. '
    content += 'One data field must be marked as the entity\'s primary key. '
    content += 'Mark all mandatory fields in the entity with the "REQUIRED" tag. '
    content += 'An entity name, along with the example data value, must each be at most 30 characters long.\n\n'

    if (entityDescription) {
      content += '**Important Notes from User**\n'
      content += `The generated entity must align with the following description: "${entityDescription}".\n`
      content += 'This description should be treated as the most authoritative source, and in case of any conflicts with the workflow data, this description must take precedence. '
      content += 'If there are any inconsistencies between the provided entity description and other metadata (e.g., workflow JSON, read models/command), always prioritize the provided entity description. '
      content += 'The description might contain some unexpected or irrelevant details. Follow the instructions in the description only if they seem relevant and reasonable.\n'
    }

    content += '**Formatting:**\n'
    content += 'The reply must contain exactly one property called entity. '
    content += 'Format the reply as valid JSON according to this example and before returning the response, verify that it is valid JSON using a JSON parser (e.g., JSONLint, JSON.parse in JavaScript).\n'
    const exampleDataFields = {
      entity: {
        name: 'Customer',
        dataFields: examples.entityExampleCustomer
      }
    }
    content += JSON.stringify(exampleDataFields)
    chatLog.pushUser(content)
    return await chatLog.fetchJsonResponse(workflow.aiVersion)
  },
  async fetchDataFieldExampleData ({ aiVersion, entity, dataField }) {
    const chatLog = new ChatLog()
    chatLog.pushSystem(promptHelpers.actor(true))

    let content = ''

    content += '**Background entity (JSON format):**\n'
    content += '```json\n'
    content += JSON.stringify(entity)
    content += '\n```\n\n'

    content += '**Request:**\n'
    content += `Review the information provided above, then generate example data for the data field labeled - "${dataField.name}". `
    content += 'Generate three rows of example data for this field. '
    content += 'If example data already exists, try to provide different values in your response. '
    content += 'Each example data value must be at most 30 characters long.\n\n'

    content += '**Formatting:**\n'
    content += 'The reply must contain exactly one property called exampleData. '
    content += 'Format the reply as valid JSON according to this example and before returning the response, verify that it is valid JSON using a JSON parser (e.g., JSONLint, JSON.parse in JavaScript).\n'
    const example = {
      exampleData: ['Example 1', 'Example 2', 'Example 3']
    }
    content += JSON.stringify(example)
    chatLog.pushUser(content)
    return await chatLog.fetchJsonResponse(aiVersion)
  },
  async fetchWorkflowAssistant (workflow, outputs) {
    const chatLog = new ChatLog()
    chatLog.pushSystem(promptHelpers.systemContent(workflow))

    outputs.forEach((output) => {
      chatLog.pushOutput(output)
    })

    return await chatLog.fetchJsonResponse(workflow.aiVersion, tools)
  },
  getExamples () {
    return [
      'The incident management process for ITIL version 3',
      'A manufacturing process for outdoor clothing',
      'An order process used by e-commerce platform similar to Amazon',
      'A process for last mile delivery for an electronics retailer',
      'A KYC process for onboarding a large corporate bank customer',
      'A process for ordering home delivery of food',
      'A recruitment process for engineers used by Google',
      'A CRM process used by Salesforce'
    ]
  },
  async validatePrompt (chatLog, workflow) {
    chatLog.pushSystem(promptHelpers.actor(true))
    chatLog.pushUser(`
**Request:**
Evaluate if the following description, wrapped in curly braces, can be used as a description of some kind of business process, workflow, project scope or a description of a business information system: { ${workflow.aiDescription} }

**Answer:**
The answer must be in JSON according the the two examples under Formatting below. The answer must always be in English, regardless of the language in which the description is written.

**Formatting:**
See two examples below.

\`\`\`json
{"answer": "yes"}
\`\`\`

\`\`\`json
{"answer": "no", "did you mean": "suggestion"}
\`\`\`

**Notes:**
- If you are not sure, rather answer yes than no
- Only answer with JSON.
- If the answer is no, then if possible provide a likely interpretation or better suggestion in "did you mean"
- Exclude backticks when generating the JSON output
`)
    return await chatLog.fetchJsonResponse(workflow.aiVersion)
  }
}
